import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Download, FileText, Calendar, Landmark, Loader2, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Reviewer() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [needEmail, setNeedEmail] = useState(false);
  const [email, setEmail] = useState("");

  const fetchData = useCallback(async (withEmail) => {
    setLoading(true); setError("");
    try {
      const params = withEmail ? { email: withEmail } : {};
      const res = await axios.get(`${BACKEND}/api/review/${token}`, { params });
      if (res.data.require_email) { setNeedEmail(true); setData(res.data); }
      else { setNeedEmail(false); setData(res.data); }
    } catch (e) {
      setError(e.response?.data?.detail || "This link is unavailable.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const dl = (evId) => {
    const q = needEmail || email ? `?email=${encodeURIComponent(email)}` : "";
    window.open(`${BACKEND}/api/review/${token}/download/${evId}${q}`, "_blank");
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
      <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#2E82F6" }} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#070B14" }}>
      <div className="max-w-md text-center space-y-3" data-testid="reviewer-error">
        <Lock className="h-10 w-10 mx-auto" style={{ color: "#64748B" }} />
        <p className="text-lg" style={{ color: "#E2E8F0" }}>{error}</p>
      </div>
    </div>
  );

  if (needEmail) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#070B14" }}>
      <div className="max-w-sm w-full space-y-4 border rounded-xl p-6" style={{ background: "#0E1526", borderColor: "#1E293B" }} data-testid="reviewer-email-gate">
        <ShieldCheck className="h-8 w-8" style={{ color: "#2E82F6" }} />
        <h1 className="text-xl font-semibold" style={{ color: "#F1F5F9" }}>{data?.matter_title || "Secure Review"}</h1>
        <p className="text-sm" style={{ color: "#94A3B8" }}>Enter your email to access this secure review link.</p>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lawfirm.com" data-testid="reviewer-email-input"
          className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "#070B14", border: "1px solid #1E293B", color: "#F1F5F9" }} />
        <button onClick={() => fetchData(email)} disabled={!email.includes("@")} data-testid="reviewer-email-submit"
          className="w-full rounded-md py-2 text-sm font-medium" style={{ background: "#2E82F6", color: "#fff" }}>Access records</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "#070B14", color: "#E2E8F0" }} data-testid="reviewer-portal">
      <header className="border-b sticky top-0 z-10" style={{ background: "#0E1526", borderColor: "#1E293B" }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6" style={{ color: "#2E82F6" }} />
          <div>
            <div className="font-semibold" style={{ color: "#F1F5F9" }}>Secure Professional Review™</div>
            <div className="text-xs" style={{ color: "#64748B" }}>{data.scope_label} · {data.matter_title} · read-only</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {data.incidents?.length > 0 && (
          <section data-testid="reviewer-timeline">
            <h2 className="text-sm font-mono tracking-widest mb-3" style={{ color: "#64748B" }}>CHRONOLOGY</h2>
            <div className="space-y-3">
              {data.incidents.map((i) => (
                <div key={i.id} className="border rounded-lg p-4" style={{ background: "#0E1526", borderColor: "#1E293B" }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "#64748B" }}>
                    <Calendar className="h-3.5 w-3.5" /> {(i.occurred_at || "").slice(0, 10)} · {i.entry_type}
                  </div>
                  <p className="text-sm rounded-md p-2" style={{ background: "rgba(46,130,246,0.10)", border: "1px solid rgba(46,130,246,0.3)" }}>{i.fact}</p>
                  {i.child_impact?.length > 0 && <div className="mt-2 text-xs" style={{ color: "#A78BFA" }}>Child impact: {i.child_impact.join("; ")}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {data.evidence?.length > 0 && (
          <section data-testid="reviewer-evidence">
            <h2 className="text-sm font-mono tracking-widest mb-3" style={{ color: "#64748B" }}>EXHIBITS</h2>
            <div className="space-y-2">
              {data.evidence.map((e) => (
                <div key={e.id} className="border rounded-lg p-3 flex items-center gap-3" style={{ background: "#0E1526", borderColor: "#1E293B" }} data-testid={`reviewer-ev-${e.id}`}>
                  <FileText className="h-4 w-4 shrink-0" style={{ color: "#64748B" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate" style={{ color: "#F1F5F9" }}>{e.filename}</div>
                    <div className="text-[10px] font-mono truncate" style={{ color: "#64748B" }}>SHA-256 {e.sha256?.slice(0, 24)}…</div>
                  </div>
                  {data.allow_downloads && (
                    <button onClick={() => dl(e.id)} data-testid={`reviewer-download-${e.id}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md shrink-0" style={{ background: "#2E82F6", color: "#fff" }}>
                      <Download className="h-3.5 w-3.5" /> Download Raw Source
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {data.financial?.length > 0 && (
          <section data-testid="reviewer-financial">
            <h2 className="text-sm font-mono tracking-widest mb-3" style={{ color: "#64748B" }}>FINANCIAL DAMAGES</h2>
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: "#1E293B" }}>
              {data.financial.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-3 p-3 border-b" style={{ background: "#0E1526", borderColor: "#1E293B" }}>
                  <div className="min-w-0"><div className="text-sm truncate" style={{ color: "#F1F5F9" }}>{f.description}</div><div className="text-[10px]" style={{ color: "#64748B" }}>{f.date} · {f.category} · {f.recovery_status}</div></div>
                  <div className="font-semibold shrink-0" style={{ color: "#2E82F6" }}>${Number(f.amount).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(!data.incidents?.length && !data.evidence?.length && !data.financial?.length) && (
          <p className="text-center text-sm py-12" style={{ color: "#64748B" }}>No records are included in this link.</p>
        )}

        <footer className="pt-6 border-t text-center" style={{ borderColor: "#1E293B" }}>
          <div className="flex items-center justify-center gap-2 text-xs" style={{ color: "#64748B" }}>
            <Landmark className="h-3.5 w-3.5" /> Shared via EvidenceKeeper™ · Not legal advice. This is a documentation tool, not a substitute for an attorney.
          </div>
        </footer>
      </main>
    </div>
  );
}
